import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES, logFormatter } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Virtual Machine CPU Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['statusCpuUtil', 'avgCpu', 'vmCpuUsage'],
    },
};

const PERCENT = 'PERCENT';
const FREQUENCY = 'FREQUENCY';

const typeConfig = {
    [PERCENT]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (%)`,
                },
                ceiling: 100,
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusCpuUtil',
        chartTrends: [
            {
                formatter: FORMATTER_TYPES.PERCENT,
                units: '%',
                chartType: 'spline',
                key: 'avgCpu',
                label: t`Usage`,
                multiplier: 1,
                forceDecimals: true,
            },
        ],
    },
    [FREQUENCY]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (Hz)`,
                },
                labels: {
                    formatter: (data) => logFormatter(data.value, { multiplier: 1000000 }),
                },
                ceiling: undefined,
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusCpuUtil',
        chartTrends: [
            {
                formatter: FORMATTER_TYPES.LOG,
                units: 'Hz',
                chartType: 'spline',
                key: 'vmCpuUsage',
                label: t`Usage`,
                multiplier: 1000000,
                forceDecimals: true,
            },
        ],
    },
};

function useChartConfig(type) {
    const colorCpu = useMetricColor('serverCpu');

    return useMemo(() => {
        const config = typeConfig[type];
        config.chartTrends[0].color = colorCpu;
        return config;
    }, [type, colorCpu]);
}

const selections = [
    { value: PERCENT, label: '%' },
    { value: FREQUENCY, label: t`Frequency` },
];

function VirtualMachineCpuUtilizationPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig(chartDisplayType);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

VirtualMachineCpuUtilizationPanel.propTypes = panelPropTypes;

VirtualMachineCpuUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmCpuUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`CPU utilization trend for a selected virtual machine (VM)`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachineCpuUtilizationPanel);
